<template>
    <Card>
        <CardHeader>
            <div class="flex items-center gap-2">
                <Globe class="size-4 text-muted-foreground" />
                <CardTitle class="text-sm font-medium text-primary">
                    Allowed domains
                </CardTitle>
            </div>
        </CardHeader>

        <CardContent>
        <!-- Empty State -->
        <div v-if="domainsActual.length === 0" class="py-6 mb-4 text-center">
            <div class="mb-3 flex justify-center">
                <svg width="46" height="40" viewBox="0 0 46 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M28.5713 40H17.1426V28.5713H28.5713V40ZM17.1426 28.5713H5.71387V22.8574H17.1426V28.5713ZM40 28.5713H28.5713V22.8574H40V28.5713ZM5.71387 22.8574H0V5.71387H5.71387V22.8574ZM45.7139 22.8574H40V5.71387H45.7139V22.8574ZM40 5.71387H5.71387V0H40V5.71387Z" fill="#F5F5F5"/>
                </svg>

            </div>
            <p class="mb-1.5 text-sm font-medium text-foreground">
                No domains allowed
            </p>
            <p class="text-xs text-muted-foreground">
                Please add a domain to start using Plugin.
            </p>
        </div>

        <!-- Domain List -->
        <div v-else class="mb-4 space-y-2.5">
            <div
                v-for="(domain, index) in domainsActual"
                :key="index"
                class="flex min-h-10 items-center justify-between bg-new-neutral-50 px-3"
            >
                <div class="flex items-center gap-2">
                    <span class="size-1.5 shrink-0 rounded-full bg-new-green-500" />
                    <p class="text-xs text-foreground">
                        {{ domain }}
                    </p>
                </div>
                <Button
                    v-if="readyToRemove !== index"
                    variant="link"
                    size="sm"
                    class="text-new-blue-500"
                    @click="showIndex(index)"
                >
                    Remove
                </Button>
                <Button
                    v-else
                    variant="link"
                    size="sm"
                    class="text-destructive"
                    @click="removeDomain(index)"
                >
                    Really remove?
                </Button>
            </div>
        </div>

        <!-- Add Domain Form -->
        <form class="space-y-3" @submit.prevent="addDomains">
            <div class="flex gap-0">
                <Input
                    id="new-domains"
                    v-model="newDomains"
                    placeholder="example.com"
                    class="flex-1"
                    :class="{ 'border-destructive': addError }"
                />
                <Button
                    :disabled="!newDomains || adding"
                    type="submit"
                >
                    Add Domain
                </Button>
            </div>
            <p v-if="addError" class="text-xs text-destructive">{{ addError }}</p>
            <p class="text-xs text-muted-foreground">
                List allowed domains separated by comma. API Token is currently active for following domains. For more info about domains
                <a
                    href="https://docs.topol.io"
                    target="_blank"
                    class="font-semibold underline text-new-blue-500 hover:text-new-blue-600"
                >
                    check out our documentation</a
                >.
            </p>
        </form>
        </CardContent>
    </Card>
</template>

<script lang="ts" setup>
import { PropType, ref } from 'vue';
import axios from 'axios';
import { route } from 'ziggy-js';
import { toast } from 'vue-sonner';
import IToken from '@/types/Plugin/IToken';
import { Button } from '@/components/ui/button';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Globe } from 'lucide-vue-next';

const props = defineProps({
    domains: {
        type: String,
        default: '',
    },
    token: {
        type: Object as PropType<IToken>,
        required: true,
    },
});

const domainsActual = ref<string[]>(
    props.domains ? props.domains.split(',') : []
);
const readyToRemove = ref<number | null>(null);
const newDomains = ref<string>('');
const addError = ref<string>('');
const adding = ref(false);

const showIndex = (index: number) => {
    readyToRemove.value = index;
};

const extractErrorMessage = (err: unknown, fallback: string): string => {
    const e = err as {
        response?: {
            data?: {
                errors?: Record<string, string | string[]>;
                message?: string;
            };
        };
    };
    const fieldErrors = e?.response?.data?.errors;
    if (fieldErrors) {
        const messages = Object.values(fieldErrors)
            .flat()
            .filter((v): v is string => typeof v === 'string' && v.length > 0);
        if (messages.length) return messages.join(' ');
    }
    return e?.response?.data?.message ?? fallback;
};

const persistDomains = async (next: string[]): Promise<boolean> => {
    const res = await axios.put(
        route('plugin.api-tokens.update', { apiToken: props.token.id }),
        { token: props.token, domains: next.join(',') },
    );
    return res.status === 200;
};

const removeDomain = async (index: number) => {
    const previous = [...domainsActual.value];
    const next = [...domainsActual.value];
    next.splice(index, 1);

    domainsActual.value = next;
    readyToRemove.value = null;

    try {
        await persistDomains(next);
        toast.success('Domains updated successfully!');
    } catch (err) {
        // Roll back on failure so the UI matches what's actually persisted.
        domainsActual.value = previous;
        toast.error(extractErrorMessage(err, 'Failed to update domains'));
    }
};

const addDomains = async () => {
    addError.value = '';

    const incoming = newDomains.value
        .replace(/ /g, '')
        .split(',')
        .filter((d) => d !== '');

    if (!incoming.length) return;

    const combined = [...domainsActual.value, ...incoming];

    adding.value = true;
    try {
        await persistDomains(combined);
        // Only update the visible list & clear input once the backend has accepted.
        domainsActual.value = combined;
        newDomains.value = '';
        toast.success('Domains updated successfully!');
    } catch (err) {
        const message = extractErrorMessage(err, 'Failed to add domains');
        addError.value = message;
        toast.error(message);
        // Keep newDomains as-is so the user can fix the offending input and retry.
    } finally {
        adding.value = false;
    }
};
</script>
